import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { FileUpload } from 'primereact/fileupload';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MapContainer, TileLayer, CircleMarker, useMap, useMapEvents } from 'react-leaflet';
import { fetchCategories } from '../apis/home/category';
import 'leaflet/dist/leaflet.css';

interface VendorFormDialogProps {
    visible: boolean;
    mode: 'create' | 'edit';
    loading?: boolean;
    onHide: () => void;
    onSubmit: (vendor: Record<string, any>) => Promise<void> | void;
    vendor?: any;
}

const emptyForm = {
    shopName: '',
    shopAddress: '',
    lat: '',
    lng: '',
    name: '',
    phoneNumber: '',
    email: '',
    aadharNumber: '',
    panNumber: '',
    pincode: '',
    category: '',
    isOnline: false,
    isFeatured: false,
    status: 'pending',
};

function buildSearchKeywords(shopName: string) {
    const name = shopName.trim().toLowerCase();
    const words = name.split(/\s+/);
    const keywords: string[] = [];

    for (const word of words) {
        for (let i = 1; i <= word.length; i++) {
            keywords.push(word.slice(0, i));
        }
    }

    keywords.push(name);

    return [...new Set(keywords)];
}

const defaultMapCenter: [number, number] = [17.385, 78.4867];

const MapController: React.FC<{
    center: [number, number];
    onPick: (lat: number, lng: number) => void;
}> = ({ center, onPick }) => {
    const map = useMap();

    useMapEvents({
        click(event) {
            onPick(event.latlng.lat, event.latlng.lng);
        },
    });

    useEffect(() => {
        map.setView(center, 15);
    }, [center, map]);

    return null;
};

const VendorFormDialog: React.FC<VendorFormDialogProps> = ({ visible, mode, loading = false, onHide, onSubmit, vendor }) => {
    const [form, setForm] = useState({ ...emptyForm });
    const [shopImageFile, setShopImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState('');
    const fileUploadRef = useRef<any>(null);

    const categoriesQuery = useQuery({
        queryKey: ['vendor-form', 'categories'],
        queryFn: async () => {
            const categories = await fetchCategories();
            return Array.isArray(categories) ? categories : [];
        },
        enabled: visible,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (!visible) {
            return;
        }

        if (mode === 'edit' && vendor) {
            const shopGps = vendor.shopGps || {};
            setForm({
                shopName: vendor.shopName || '',
                shopAddress: vendor.shopAddress || '',
                lat: shopGps.lat !== undefined && shopGps.lat !== null ? String(shopGps.lat) : '',
                lng: shopGps.lng !== undefined && shopGps.lng !== null ? String(shopGps.lng) : '',
                name: vendor.name || '',
                phoneNumber: vendor.phoneNumber || '',
                email: vendor.email || '',
                aadharNumber: vendor.aadharNumber || '',
                panNumber: vendor.panNumber || '',
                pincode: vendor.pincode || '',
                category: vendor.category || '',
                isOnline: Boolean(vendor.isOnline),
                isFeatured: Boolean(vendor.isFeatured),
                status: vendor.status || 'pending',
            });
            setShopImageFile(null);
            setImagePreview(vendor.shopImage || '');
            if (fileUploadRef.current) {
                fileUploadRef.current.clear();
            }
            return;
        }

        setForm({ ...emptyForm });
        setShopImageFile(null);
        setImagePreview('');
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
    }, [visible, mode, vendor]);

    const setField = (name: string, value: any) => {
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageSelect = (event: any) => {
        const file = event.files?.[0];
        if (!file) return;

        setShopImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const clearImage = () => {
        setShopImageFile(null);
        setImagePreview('');
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
    };

    const parseNumber = (value: string) => {
        if (value === '') return undefined;
        const parsed = Number(value);
        return Number.isNaN(parsed) ? undefined : parsed;
    };

    const latValue = parseNumber(form.lat);
    const lngValue = parseNumber(form.lng);
    const hasCoordinates = latValue !== undefined && lngValue !== undefined;
    const mapCenter: [number, number] = hasCoordinates ? [latValue, lngValue] : defaultMapCenter;

    const buildPayload = () => {
        const searchKeywords = buildSearchKeywords(form.shopName);
        const payload = new FormData();
        payload.append('shopName', form.shopName.trim());
        payload.append('shopAddress', form.shopAddress.trim());
        payload.append('name', form.name.trim());
        payload.append('phoneNumber', form.phoneNumber.trim());
        payload.append('email', form.email.trim());
        payload.append('aadharNumber', form.aadharNumber.trim());
        payload.append('panNumber', form.panNumber.trim());
        payload.append('pincode', form.pincode.trim());
        payload.append('category', form.category.trim());
        payload.append('searchKeywords', JSON.stringify(searchKeywords));
        payload.append('isOnline', String(form.isOnline));
        payload.append('isFeatured', String(form.isFeatured));
        payload.append('status', form.status);

        const lat = parseNumber(form.lat);
        const lng = parseNumber(form.lng);
        if (lat !== undefined && lng !== undefined) {
            payload.append('shopGps', JSON.stringify({ lat, lng }));
        }

        if (shopImageFile) {
            payload.append('shopImage', shopImageFile, shopImageFile.name);
        }

        return payload;
    };

    const validateForm = () => {
        const requiredFields: Array<[string, string]> = [
            ['shopName', 'Shop name is required'],
            ['shopAddress', 'Shop address is required'],
            ['name', 'Vendor name is required'],
            ['phoneNumber', 'Phone number is required'],
            ['email', 'Email is required'],
            ['category', 'Category is required'],
            ['pincode', 'Pincode is required'],
        ];

        for (const [field, message] of requiredFields) {
            if (!String(form[field as keyof typeof form] ?? '').trim()) {
                return message;
            }
        }

        if (mode === 'create' && !shopImageFile) {
            return 'Shop image is required';
        }

        if (!form.lat.trim() || !form.lng.trim()) {
            return 'Please select a valid GPS location';
        }

        return '';
    };

    const handleSubmit = async () => {
        const validationError = validateForm();
        if (validationError) {
            toast.error(validationError);
            return;
        }

        await onSubmit(buildPayload());
    };

    const categoryOptions = (categoriesQuery.data || []).map((category: any) => ({
        label: category.title || category.name || category.categoryId,
        value: category.title || category.name || category.categoryId,
    }));

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" severity="secondary" outlined onClick={onHide} type="button" />
            <Button label={mode === 'create' ? 'Create Vendor' : 'Save Changes'} icon="pi pi-check" onClick={handleSubmit} loading={loading} type="button" />
        </div>
    );

    return (
        <Dialog
            header={mode === 'create' ? 'Create Vendor' : 'Edit Vendor'}
            visible={visible}
            onHide={onHide}
            modal
            maximizable
            style={{ width: '72vw', minWidth: '920px' }}
            footer={footer}
            className="p-fluid"
        >
            <div className="grid">
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Shop Name</label>
                    <InputText required value={form.shopName} onChange={(e) => setField('shopName', e.target.value)} placeholder="FRESH FRUITS" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Shop Image</label>
                    <FileUpload
                        ref={fileUploadRef}
                        mode="basic"
                        name="shopImage"
                        accept="image/*"
                        chooseLabel="Upload Image"
                        customUpload
                        auto
                        uploadHandler={handleImageSelect}
                    />
                    {imagePreview ? (
                        <div className="mt-3 flex align-items-center gap-3">
                            <img
                                src={imagePreview}
                                alt="Shop preview"
                                style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '12px' }}
                            />
                            <Button label="Remove" severity="secondary" outlined type="button" onClick={clearImage} />
                        </div>
                    ) : (
                        <small className="text-600 block mt-2">
                            {mode === 'create'
                                ? 'Upload a shop image. This is required before the vendor can be created.'
                                : 'Upload a new shop image only if you want to replace the current one.'}
                        </small>
                    )}
                </div>
                <div className="col-12">
                    <label className="block mb-2 font-medium">Shop Address</label>
                    <InputTextarea required rows={3} value={form.shopAddress} onChange={(e) => setField('shopAddress', e.target.value)} placeholder="Shop address" />
                </div>
                <div className="col-12">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <label className="block font-medium mb-0">Pick GPS Location</label>
                        <small className="text-600">Click the map to set latitude and longitude</small>
                    </div>
                    <div className="border-round overflow-hidden surface-100 border-1 surface-border">
                        <MapContainer
                            center={mapCenter}
                            zoom={hasCoordinates ? 15 : 5}
                            scrollWheelZoom
                            style={{ width: '100%', height: '360px' }}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <MapController
                                center={mapCenter}
                                onPick={(lat, lng) => {
                                    setField('lat', String(lat.toFixed(6)));
                                    setField('lng', String(lng.toFixed(6)));
                                }}
                            />
                            {hasCoordinates && (
                                <CircleMarker
                                    center={mapCenter}
                                    radius={10}
                                    pathOptions={{ color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 0.35 }}
                                />
                            )}
                        </MapContainer>
                        <div className="p-2 text-sm text-600 flex justify-content-between align-items-center">
                            <span>
                                {hasCoordinates ? `${latValue}, ${lngValue}` : 'No location selected yet'}
                            </span>
                            <span>Tap on the map to choose a location</span>
                        </div>
                    </div>
                    <div className="mt-2 flex justify-content-end">
                        <Button
                            label="Use Default Center"
                            severity="secondary"
                            outlined
                            type="button"
                            onClick={() => {
                                setField('lat', String(defaultMapCenter[0]));
                                setField('lng', String(defaultMapCenter[1]));
                            }}
                        />
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Name</label>
                    <InputText required value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Vendor name" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Phone Number</label>
                    <InputText required value={form.phoneNumber} onChange={(e) => setField('phoneNumber', e.target.value)} placeholder="+918686479252" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Email</label>
                    <InputText required value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="vendor@example.com" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Category</label>
                    <Dropdown
                        required
                        value={form.category}
                        options={categoryOptions}
                        onChange={(e) => setField('category', e.value)}
                        placeholder="Select category"
                        filter
                        showClear
                    />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Aadhar Number</label>
                    <InputText value={form.aadharNumber} onChange={(e) => setField('aadharNumber', e.target.value)} placeholder="441646770024" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">PAN Number</label>
                    <InputText value={form.panNumber} onChange={(e) => setField('panNumber', e.target.value)} placeholder="DLUPM8177A" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Pincode</label>
                    <InputText required value={form.pincode} onChange={(e) => setField('pincode', e.target.value)} placeholder="500036" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Status</label>
                    <Dropdown
                        value={form.status}
                        options={[
                            { label: 'Pending', value: 'pending' },
                            { label: 'Verified', value: 'verified' },
                            { label: 'Blocked', value: 'blocked' },
                        ]}
                        onChange={(e) => setField('status', e.value)}
                        placeholder="Select status"
                    />
                </div>
                <div className="col-12 md:col-4">
                    <label className="block mb-2 font-medium">Online</label>
                    <div className="flex align-items-center gap-3">
                        <InputSwitch checked={form.isOnline} onChange={(e) => setField('isOnline', e.value)} />
                        <span>{form.isOnline ? 'Online' : 'Offline'}</span>
                    </div>
                </div>
                <div className="col-12 md:col-4">
                    <label className="block mb-2 font-medium">Featured</label>
                    <div className="flex align-items-center gap-3">
                        <InputSwitch checked={form.isFeatured} onChange={(e) => setField('isFeatured', e.value)} />
                        <span>{form.isFeatured ? 'Featured' : 'Not featured'}</span>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default VendorFormDialog;
